<script setup lang="ts">
import type { Placement } from "floating-vue";
import type VDropdown from "../Dropdown/VDropdown.vue";
import { useMediaQuery } from "@vueuse/core";
import { ref, shallowRef } from "vue";
import VBottomSheet from "../BottomSheet/VBottomSheet.vue";
import VCloseButton from "../CloseButton/VCloseButton.vue";
import { useDialogFocusContainer } from "../Dialog/dialogFocusContainers";

/**
 * The popover of the date and time pickers. From 640px wide it is a floating-vue dropdown under the field; below it
 * is a bottom sheet with a grabber, a title and a close button. The owner opens and closes it and handles clicks
 * outside; the sheet closes itself on its backdrop, a drag down and Escape.
 */
const {
  label,
  placement = "bottom-start",
  referenceNode = null,
  disabled = false,
} = defineProps<{
  /** Name of the dialog: the popover's accessible name and the sheet's visible title. */
  label: string
  placement?: Placement
  /** Anchors the popover to another element than the default slot, such as the whole field. */
  referenceNode?: (() => Element) | null
  disabled?: boolean
}>();

const emit = defineEmits<{
  show: []
  hide: []
}>();

defineSlots<{
  default?: () => unknown
  panel?: (props: { sheet: boolean }) => unknown
}>();

/** Tailwind's `sm` screen: below it a popover becomes a bottom sheet. */
const isSheet = useMediaQuery("(max-width: 639px)");
const dropdown = ref<InstanceType<typeof VDropdown> | null>(null);
const sheetOpen = ref(false);
const isOpen = ref(false);

// The panel lives under `body` (popper or sheet), so a surrounding dialog's focus trap has to know it.
const panelRoot = shallowRef<HTMLElement | null>(null);
useDialogFocusContainer(panelRoot);

function onShow(): void {
  isOpen.value = true;
  emit("show");
}

function onHide(): void {
  if (!isOpen.value) {
    return;
  }
  isOpen.value = false;
  emit("hide");
}

function show(): void {
  if (disabled || isOpen.value) {
    return;
  }
  if (isSheet.value) {
    sheetOpen.value = true;
    onShow();
    return;
  }
  void dropdown.value?.show();
}

function hide(): void {
  if (sheetOpen.value) {
    sheetOpen.value = false;
    onHide();
    return;
  }
  dropdown.value?.hide();
}

function onSheetOpen(open: boolean): void {
  sheetOpen.value = open;
  if (!open) {
    onHide();
  }
}

const sheetZIndex = 10010;

defineExpose({ show, hide, isOpen });
</script>

<template>
  <VDropdown
    ref="dropdown"
    class="w-full"
    :placement="placement"
    theme="verbleif-popper"
    popper-class="v-datepicker-popper"
    no-auto-focus
    :auto-hide="false"
    :triggers="[]"
    :reference-node="referenceNode"
    :disabled="disabled"
    @show="onShow"
    @hide="onHide"
  >
    <slot />
    <template #popper>
      <div
        v-if="!isSheet"
        ref="panelRoot"
        role="dialog"
        :aria-label="label"
        class="p-3"
        @click.stop
        @mousedown.stop
      >
        <slot name="panel" :sheet="false" />
      </div>
    </template>
  </VDropdown>
  <Teleport v-if="isSheet" to="body">
    <div ref="panelRoot" class="v-picker-sheet">
      <VBottomSheet
        :open="sheetOpen"
        :aria-label="label"
        tone="plain"
        :z-index="sheetZIndex"
        @update:open="onSheetOpen"
      >
        <template #header>
          <div class="flex items-center justify-between px-4 pt-1">
            <h2 class="m-0 text-base leading-5 font-semibold">
              {{ label }}
            </h2>
            <VCloseButton @pointerdown.stop @click="hide" />
          </div>
        </template>
        <div class="px-4">
          <slot name="panel" :sheet="true" />
        </div>
      </VBottomSheet>
    </div>
  </Teleport>
</template>
